import type { InjectionKey, Ref } from "vue";
import type { useHydraInfiniteCollection } from "./useHydraInfiniteCollection";
import { inject, provide } from "vue";

export interface TableColumn {
  label: string
  width?: string | number | "auto"
  align?: "left" | "right"
  hint?: string
  customGetter?: (item: any) => string
}

export type TableField = TableColumn | string;

/**
 * Loads more rows of a list that grows while scrolling. VTable calls it when its rows scroll near the end (and, for a
 * window that dropped its first pages, near the start), and shows the total in its footer.
 */
export interface CollectionLoadMore {
  hasNextPage: Readonly<Ref<boolean>>
  fetchNextPage: () => Promise<unknown> | void
  hasPreviousPage: Readonly<Ref<boolean>>
  fetchPreviousPage: () => Promise<unknown> | void
  isFetchingPrevious: Readonly<Ref<boolean>>
  /** Total rows on the server; 0 until the first page is known. */
  totalItems: Readonly<Ref<number>>
}

export interface CollectionContext<T = unknown> {
  items: Readonly<Ref<T[]>>
  loading: Readonly<Ref<boolean>>
  isFetchingMore: Readonly<Ref<boolean>>
  hasError: Readonly<Ref<boolean>>
  errorCode: Readonly<Ref<number | undefined>>
  errorMsg: Readonly<Ref<string>>
  tableFields: Record<string, TableField> | undefined
  refresh: () => void | Promise<unknown>
  loadMore?: CollectionLoadMore
}

export const CollectionContextKey: InjectionKey<CollectionContext> = Symbol("CollectionContext");

export function provideCollectionContext<T>(ctx: CollectionContext<T>): CollectionContext<T> {
  provide(CollectionContextKey, ctx as CollectionContext);
  return ctx;
}

export function useCollectionContext<T>(): CollectionContext<T> {
  const instance = inject(CollectionContextKey);
  if (!instance) {
    throw new Error("Run provideCollectionContext before useCollectionContext.");
  }
  return instance as CollectionContext<T>;
}

export function collectionContextFromInfinite<T extends { "@id": string }>(
  q: ReturnType<typeof useHydraInfiniteCollection<T>>,
  tableFields?: Record<string, TableField>,
): CollectionContext<T> {
  return {
    items: q.items,
    loading: q.loading,
    isFetchingMore: q.isFetchingNextPage,
    hasError: q.hasError,
    errorCode: q.errorCode,
    errorMsg: q.errorMsg,
    tableFields,
    refresh: () => q.refetch(),
    loadMore: loadMoreFromInfinite(q),
  };
}

/** The paging of an infinite collection for a table that builds its own context from mapped rows. */
export function loadMoreFromInfinite<T extends { "@id": string }>(
  q: ReturnType<typeof useHydraInfiniteCollection<T>>,
): CollectionLoadMore {
  return {
    hasNextPage: q.hasNextPage,
    // A second call while a page loads must not cancel and restart that request.
    fetchNextPage: () => q.fetchNextPage({ cancelRefetch: false }),
    hasPreviousPage: q.hasPreviousPage,
    fetchPreviousPage: () => q.fetchPreviousPage({ cancelRefetch: false }),
    isFetchingPrevious: q.isFetchingPreviousPage,
    totalItems: q.totalItems,
  };
}
